import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { 
  FileText, 
  Plus, 
  Search, 
  Calendar,
  Clock,
  Euro,
  CheckCircle2,
  AlertCircle,
  Send,
  Loader2,
  ChevronDown,
  ChevronRight,
  Star,
  TrendingUp,
  TrendingDown,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

const MonthlyReportsPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [members, setMembers] = useState([]);
  
  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMember, setFilterMember] = useState('all');
  
  // Generate dialog
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [selectedContract, setSelectedContract] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [generating, setGenerating] = useState(false);
  
  // Validation dialog
  const [showValidateDialog, setShowValidateDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [taskAdjustments, setTaskAdjustments] = useState({});
  const [globalBonus, setGlobalBonus] = useState(0);
  const [globalBonusType, setGlobalBonusType] = useState('fixed'); // 'fixed' or 'percent'
  const [globalBonusReason, setGlobalBonusReason] = useState('');
  const [agencyNotes, setAgencyNotes] = useState('');
  const [validating, setValidating] = useState(false);
  
  // Expanded reports
  const [expandedReports, setExpandedReports] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [reportsRes, contractsRes, membersRes] = await Promise.all([
        api.get('/monthly-reports/'),
        api.get('/member-contracts/'),
        api.get('/users/team-members')
      ]);
      setReports(reportsRes.data || []);
      setContracts(contractsRes.data || []);
      setMembers(membersRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  };

  const handleGenerate = async () => {
    if (!selectedContract || !selectedMonth) {
      toast.error('Veuillez sélectionner un contrat et un mois');
      return;
    }
    
    setGenerating(true);
    try {
      const response = await api.post('/monthly-reports/generate', {
        contract_id: selectedContract,
        month: selectedMonth
      });
      toast.success(response.data.message);
      setShowGenerateDialog(false);
      setSelectedContract('');
      setSelectedMonth('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const openValidateDialog = (report) => {
    setSelectedReport(report);
    setTaskAdjustments({});
    setGlobalBonus(0);
    setGlobalBonusReason('');
    setAgencyNotes('');
    setShowValidateDialog(true);
  };

  const handleValidate = async () => {
    if (!selectedReport) return;
    
    setValidating(true);
    try {
      const adjustments = Object.entries(taskAdjustments).map(([taskId, adj]) => ({
        task_id: taskId,
        bonus_malus: parseFloat(adj.amount) || 0,
        reason: adj.reason || ''
      })).filter(a => a.bonus_malus !== 0);
      
      await api.put(`/monthly-reports/${selectedReport.id}/validate`, {
        task_adjustments: adjustments,
        global_bonus: parseFloat(globalBonus) || 0,
        global_bonus_reason: globalBonusReason,
        agency_notes: agencyNotes
      });
      
      toast.success('Rapport validé avec succès');
      setShowValidateDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setValidating(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      'draft': { label: 'Brouillon', className: 'bg-slate-100 text-slate-700' },
      'pending_validation': { label: 'À valider', className: 'bg-amber-100 text-amber-700' },
      'validated': { label: 'Validé', className: 'bg-green-100 text-green-700' },
      'paid': { label: 'Payé', className: 'bg-blue-100 text-blue-700' }
    };
    const c = config[status] || config['draft'];
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const toggleReportExpand = (reportId) => {
    setExpandedReports(prev => ({
      ...prev,
      [reportId]: !prev[reportId]
    }));
  };

  const calculatePreviewTotal = () => {
    if (!selectedReport) return 0;
    let total = selectedReport.base_amount || 0;
    
    // Add task adjustments
    Object.entries(taskAdjustments).forEach(([taskId, adj]) => {
      const task = selectedReport.tasks?.find(t => t.task_id === taskId);
      const taskAmount = task ? ((task.consumed_minutes || task.duration_minutes || 0) / 60) * (selectedReport.hourly_rate || 30) : 0;
      
      if (adj.type === 'percent') {
        total += taskAmount * (parseFloat(adj.amount) || 0) / 100;
      } else {
        total += parseFloat(adj.amount) || 0;
      }
    });
    
    // Add global bonus
    if (globalBonusType === 'percent') {
      total += (selectedReport.base_amount || 0) * (parseFloat(globalBonus) || 0) / 100;
    } else {
      total += parseFloat(globalBonus) || 0;
    }
    
    return total;
  };

  const filteredReports = reports.filter(report => {
    if (filterStatus !== 'all' && report.status !== filterStatus) return false;
    if (filterMember !== 'all' && report.member_id !== filterMember) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      <main className={cn(
        "transition-all duration-300 px-4 md:px-6 pb-6",
        collapsed ? "md:ml-20" : "md:ml-64"
      )} style={{ paddingTop: "130px" }}>
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
              Rapports mensuels
            </h1>
            <p className="text-slate-500">Suivi d'activité et validation des heures</p>
          </div>
          <Button 
            className="bg-amber-600 hover:bg-amber-700"
            onClick={() => setShowGenerateDialog(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Générer un rapport
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <Label className="text-xs text-slate-500 mb-1">Statut</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous les statuts" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="draft">Brouillons</SelectItem>
                    <SelectItem value="pending_validation">À valider</SelectItem>
                    <SelectItem value="validated">Validés</SelectItem>
                    <SelectItem value="paid">Payés</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label className="text-xs text-slate-500 mb-1">Membre</Label>
                <Select value={filterMember} onValueChange={setFilterMember}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous les membres" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les membres</SelectItem>
                    {members.map(m => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.first_name} {m.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reports List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Rapports ({filteredReports.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : filteredReports.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Aucun rapport trouvé</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setShowGenerateDialog(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Générer un rapport
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredReports.map((report) => (
                  <div key={report.id} className="border rounded-lg overflow-hidden">
                    {/* Report Header */}
                    <div 
                      className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100"
                      onClick={() => toggleReportExpand(report.id)}
                    >
                      <div className="flex items-center gap-4">
                        {expandedReports[report.id] ? (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        )}
                        <div>
                          <h3 className="font-semibold text-slate-800">
                            {report.month_label || report.month}
                          </h3>
                          <p className="text-sm text-slate-500 flex items-center gap-2">
                            <User className="w-3 h-3" />
                            {report.member_name || 'Membre inconnu'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-slate-500">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {report.total_hours?.toFixed(1) || 0}h
                          </p>
                          <p className="font-bold text-lg" style={{ color: theme.colors?.accent }}>
                            {report.final_amount?.toFixed(2) || 0}€
                          </p>
                        </div>
                        {getStatusBadge(report.status)}
                      </div>
                    </div>
                    
                    {/* Expanded Content */}
                    {expandedReports[report.id] && (
                      <div className="p-4 border-t">
                        {/* Summary */}
                        <div className="grid grid-cols-4 gap-4 mb-4">
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <p className="text-xs text-slate-500">Heures</p>
                            <p className="text-lg font-bold">{report.total_hours?.toFixed(1) || 0}h</p>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <p className="text-xs text-slate-500">Taux horaire</p>
                            <p className="text-lg font-bold">{report.hourly_rate || 0}€/h</p>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <p className="text-xs text-slate-500">Base</p>
                            <p className="text-lg font-bold">{report.base_amount?.toFixed(2) || 0}€</p>
                          </div>
                          <div className="p-3 rounded-lg" style={{ backgroundColor: `${theme.colors?.accent}15` }}>
                            <p className="text-xs" style={{ color: theme.colors?.accent }}>Total</p>
                            <p className="text-lg font-bold" style={{ color: theme.colors?.accent }}>
                              {report.final_amount?.toFixed(2) || 0}€
                            </p>
                          </div>
                        </div>
                        
                        {/* Margin Summary for Agency */}
                        {report.tasks && report.tasks.length > 0 && (
                          <div className="mb-4 p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg border border-emerald-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-emerald-600" />
                                <span className="font-medium text-emerald-800">Marge générée sur ce rapport</span>
                              </div>
                              <div className="text-right">
                                {(() => {
                                  const clientRate = report.client_hourly_rate || 120;
                                  const memberRate = report.hourly_rate || 30;
                                  const totalMargin = (report.tasks || []).reduce((sum, task) => {
                                    const actualMinutes = task.consumed_minutes || task.duration_minutes || 0;
                                    const taskClientRate = task.client_hourly_rate || clientRate;
                                    return sum + ((actualMinutes / 60) * (taskClientRate - memberRate));
                                  }, 0);
                                  const marginPercent = report.base_amount > 0 
                                    ? ((totalMargin / report.base_amount) * 100) 
                                    : 0;
                                  return (
                                    <>
                                      <p className="text-2xl font-bold text-emerald-600">
                                        +{totalMargin.toFixed(0)}€
                                      </p>
                                      <p className="text-xs text-emerald-500">
                                        {marginPercent.toFixed(0)}% de marge
                                      </p>
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Tasks */}
                        {report.tasks && report.tasks.length > 0 && (
                          <div className="mb-4">
                            <h4 className="font-medium mb-2 text-slate-700">Tâches ({report.tasks.length})</h4>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Tâche</TableHead>
                                  <TableHead>Projet</TableHead>
                                  <TableHead className="text-right">Budget</TableHead>
                                  <TableHead className="text-right">Réel</TableHead>
                                  <TableHead className="text-center">Tendance</TableHead>
                                  <TableHead className="text-right">Montant</TableHead>
                                  <TableHead className="text-right">Marge</TableHead>
                                  <TableHead className="text-center">Note client</TableHead>
                                  <TableHead className="text-right">Bonus/Malus</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {report.tasks.map((task, idx) => {
                                  const budgetMinutes = task.budget_minutes || task.estimated_minutes || 0;
                                  const actualMinutes = task.consumed_minutes || task.duration_minutes || 0;
                                  const diff = budgetMinutes - actualMinutes;
                                  const percentDiff = budgetMinutes > 0 ? ((actualMinutes - budgetMinutes) / budgetMinutes) * 100 : 0;
                                  
                                  // Calculate amounts
                                  const memberRate = report.hourly_rate || 30;
                                  const clientRate = task.client_hourly_rate || report.client_hourly_rate || 120;
                                  const taskAmount = (actualMinutes / 60) * memberRate;
                                  const taskRevenue = (actualMinutes / 60) * clientRate;
                                  const taskMargin = taskRevenue - taskAmount;
                                  
                                  // Determine trend
                                  let trendIcon, trendColor, trendText;
                                  if (budgetMinutes === 0) {
                                    trendIcon = <Clock className="w-4 h-4" />;
                                    trendColor = 'text-slate-400';
                                    trendText = 'N/A';
                                  } else if (Math.abs(percentDiff) <= 10) {
                                    trendIcon = <CheckCircle2 className="w-4 h-4" />;
                                    trendColor = 'text-green-600';
                                    trendText = 'OK';
                                  } else if (percentDiff < 0) {
                                    trendIcon = <TrendingDown className="w-4 h-4" />;
                                    trendColor = 'text-green-600';
                                    trendText = `${Math.abs(percentDiff).toFixed(0)}% économisé`;
                                  } else {
                                    trendIcon = <TrendingUp className="w-4 h-4" />;
                                    trendColor = 'text-red-500';
                                    trendText = `+${percentDiff.toFixed(0)}% dépassé`;
                                  }
                                  
                                  return (
                                    <TableRow key={idx}>
                                      <TableCell className="font-medium max-w-[200px] truncate" title={task.task_name}>
                                        {task.task_name}
                                      </TableCell>
                                      <TableCell className="text-slate-500 max-w-[150px] truncate">{task.project_name || '-'}</TableCell>
                                      <TableCell className="text-right text-slate-500">
                                        {budgetMinutes > 0 ? `${(budgetMinutes / 60).toFixed(1)}h` : '-'}
                                      </TableCell>
                                      <TableCell className="text-right font-medium">
                                        {(actualMinutes / 60).toFixed(1)}h
                                      </TableCell>
                                      <TableCell className="text-center">
                                        <div className={`flex items-center justify-center gap-1 ${trendColor}`} title={trendText}>
                                          {trendIcon}
                                          <span className="text-xs hidden sm:inline">{trendText}</span>
                                        </div>
                                      </TableCell>
                                      <TableCell className="text-right font-mono text-sm">
                                        {taskAmount.toFixed(0)}€
                                      </TableCell>
                                      <TableCell className="text-right">
                                        <span className={`font-mono text-sm font-medium ${taskMargin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                          {taskMargin >= 0 ? '+' : ''}{taskMargin.toFixed(0)}€
                                        </span>
                                      </TableCell>
                                      <TableCell className="text-center">
                                        {task.client_rating ? (
                                          <span className="flex items-center justify-center gap-1">
                                            <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                                            {task.client_rating}
                                          </span>
                                        ) : '-'}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        {task.bonus_malus !== 0 && (
                                          <span className={task.bonus_malus > 0 ? 'text-green-600' : 'text-red-600'}>
                                            {task.bonus_malus > 0 ? '+' : ''}{task.bonus_malus}€
                                          </span>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                        
                        {/* Actions */}
                        {report.status !== 'validated' && report.status !== 'paid' && (
                          <div className="flex justify-end gap-2 pt-4 border-t">
                            <Button 
                              variant="outline"
                              onClick={() => openValidateDialog(report)}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Valider le rapport
                            </Button>
                          </div>
                        )}
                        
                        {report.status === 'validated' && (
                          <div className="flex items-center justify-between pt-4 border-t">
                            <div className="flex items-center gap-2 text-green-600">
                              <CheckCircle2 className="w-4 h-4" />
                              <span className="text-sm">Validé le {new Date(report.validated_at).toLocaleDateString('fr-FR')}</span>
                            </div>
                            {report.notification_sent && (
                              <span className="text-sm text-slate-500 flex items-center gap-1">
                                <Send className="w-3 h-3" />
                                Notification envoyée
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Generate Dialog */}
        <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Générer un rapport mensuel</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Contrat membre</Label>
                <Select value={selectedContract} onValueChange={setSelectedContract}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un contrat" />
                  </SelectTrigger>
                  <SelectContent>
                    {contracts.filter(c => c.status === 'active').map(contract => (
                      <SelectItem key={contract.id} value={contract.id}>
                        {contract.member_name || 'Membre'} - {contract.hourly_rate}€/h
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Mois</Label>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un mois" />
                  </SelectTrigger>
                  <SelectContent>
                    {getMonthOptions().map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleGenerate} 
                disabled={generating || !selectedContract || !selectedMonth}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Générer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Validate Dialog */}
        <Dialog open={showValidateDialog} onOpenChange={setShowValidateDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Valider le rapport - {selectedReport?.month_label}</DialogTitle>
            </DialogHeader>
            {selectedReport && (
              <div className="space-y-6 py-4">
                {/* Summary */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-slate-500">Membre</p>
                      <p className="font-medium">{selectedReport.member_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Base ({selectedReport.total_hours?.toFixed(1)}h × {selectedReport.hourly_rate}€)</p>
                      <p className="font-bold text-lg">{selectedReport.base_amount?.toFixed(2)}€</p>
                    </div>
                  </div>
                </div>
                
                {/* Task Adjustments */}
                {selectedReport.tasks && selectedReport.tasks.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-3">Ajustements par tâche</h4>
                    <div className="space-y-3 max-h-80 overflow-y-auto">
                      {selectedReport.tasks.map((task, idx) => {
                        const budgetMinutes = task.budget_minutes || task.estimated_minutes || 0;
                        const actualMinutes = task.consumed_minutes || task.duration_minutes || 0;
                        const percentDiff = budgetMinutes > 0 ? ((actualMinutes - budgetMinutes) / budgetMinutes) * 100 : 0;
                        const taskAmount = (actualMinutes / 60) * (selectedReport.hourly_rate || 30);
                        
                        let trendBadge;
                        if (budgetMinutes === 0) {
                          trendBadge = <Badge variant="outline" className="text-slate-400">N/A</Badge>;
                        } else if (Math.abs(percentDiff) <= 10) {
                          trendBadge = <Badge className="bg-green-100 text-green-700">OK</Badge>;
                        } else if (percentDiff < 0) {
                          trendBadge = <Badge className="bg-green-100 text-green-700">{Math.abs(percentDiff).toFixed(0)}% ↓</Badge>;
                        } else {
                          trendBadge = <Badge className="bg-red-100 text-red-700">+{percentDiff.toFixed(0)}% ↑</Badge>;
                        }
                        
                        const adjustment = taskAdjustments[task.task_id] || { amount: '', type: 'fixed', reason: '' };
                        
                        return (
                          <div key={idx} className="p-3 border rounded-lg bg-slate-50/50">
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                              <div className="flex-1 min-w-[200px]">
                                <p className="font-medium text-sm truncate">{task.task_name}</p>
                                <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                                  <span>Réel: {(actualMinutes / 60).toFixed(1)}h</span>
                                  {trendBadge}
                                  {task.client_rating && (
                                    <span className="flex items-center gap-1">
                                      <Star className="w-3 h-3 text-amber-500 fill-amber-500" /> {task.client_rating}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold" style={{ color: theme.colors?.accent }}>
                                  {taskAmount.toFixed(0)}€
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  placeholder="0"
                                  className="w-20 text-right h-8"
                                  value={adjustment.amount}
                                  onChange={(e) => setTaskAdjustments(prev => ({
                                    ...prev,
                                    [task.task_id]: { 
                                      amount: e.target.value, 
                                      type: adjustment.type || 'fixed', 
                                      reason: adjustment.reason || '' 
                                    }
                                  }))}
                                />
                                <Select
                                  value={adjustment.type || 'fixed'}
                                  onValueChange={(v) => setTaskAdjustments(prev => ({
                                    ...prev,
                                    [task.task_id]: { 
                                      amount: adjustment.amount || '', 
                                      type: v, 
                                      reason: adjustment.reason || '' 
                                    }
                                  }))}
                                >
                                  <SelectTrigger className="w-16 h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="fixed">€</SelectItem>
                                    <SelectItem value="percent">%</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <Input
                                placeholder="Raison..."
                                className="flex-1 min-w-[120px] h-8"
                                value={adjustment.reason}
                                onChange={(e) => setTaskAdjustments(prev => ({
                                  ...prev,
                                  [task.task_id]: { 
                                    amount: adjustment.amount || '', 
                                    type: adjustment.type || 'fixed', 
                                    reason: e.target.value 
                                  }
                                }))}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Global Bonus */}
                <div>
                  <h4 className="font-medium mb-3">Bonus/Malus global</h4>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        placeholder="0"
                        className="w-24 text-right"
                        value={globalBonus}
                        onChange={(e) => setGlobalBonus(e.target.value)}
                      />
                      <Select value={globalBonusType} onValueChange={setGlobalBonusType}>
                        <SelectTrigger className="w-16">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed">€</SelectItem>
                          <SelectItem value="percent">%</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      placeholder="Raison du bonus/malus global..."
                      className="flex-1 min-w-[200px]"
                      value={globalBonusReason}
                      onChange={(e) => setGlobalBonusReason(e.target.value)}
                    />
                  </div>
                  {globalBonus !== 0 && globalBonusType === 'percent' && selectedReport && (
                    <p className="text-xs text-slate-500 mt-1">
                      = {((selectedReport.base_amount || 0) * (parseFloat(globalBonus) || 0) / 100).toFixed(2)}€
                    </p>
                  )}
                </div>
                
                {/* Notes */}
                <div>
                  <Label>Notes pour le membre (optionnel)</Label>
                  <Textarea
                    placeholder="Commentaires sur ce rapport..."
                    value={agencyNotes}
                    onChange={(e) => setAgencyNotes(e.target.value)}
                    className="mt-2"
                  />
                </div>
                
                {/* Preview Total */}
                <div className="p-4 rounded-lg border-2" style={{ borderColor: theme.colors?.accent, backgroundColor: `${theme.colors?.accent}10` }}>
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Total à payer</span>
                    <span className="text-2xl font-bold" style={{ color: theme.colors?.accent }}>
                      {calculatePreviewTotal().toFixed(2)}€
                    </span>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowValidateDialog(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleValidate} 
                disabled={validating}
                className="bg-green-600 hover:bg-green-700"
              >
                {validating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Valider et notifier
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default MonthlyReportsPage;
